// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// #124 react-joyride Time Machine tutorial — pure read helpers.
//
// Read-side helpers for inspecting the `tutorials` array on a Users
// document. Kept separate from the payload builder so they can be
// imported into the IntroModal-trigger decision logic without coupling
// to mutation internals.
//
// Schema:
//   tutorials: [
//     { key, completed, completedAt, version }
//   ]
//
// Defensive against partial data: a Users doc might predate the
// migration script (or might be in a partial state mid-migration) —
// these helpers never throw on undefined / missing array.

/**
 * Finds the array element matching the tutorialKey, or undefined if
 * no such element exists. Pure lookup helper used by the other two.
 */
function findTutorialEntry(tsUserDocument, tutorialKey) {
  if (!tsUserDocument || typeof tsUserDocument !== "object") return undefined;
  if (typeof tutorialKey !== "string" || tutorialKey === "") return undefined;
  const tutorials = tsUserDocument.tutorials;
  if (!Array.isArray(tutorials)) return undefined;
  return tutorials.find(
    (entry) =>
      entry && typeof entry === "object" && entry.key === tutorialKey
  );
}

/**
 * Returns true when the tutorial has been completed for this user.
 * Defaults to `false` for any missing / malformed state so the user
 * sees the tour rather than silently skipping it.
 */
function isTutorialCompleted(tsUserDocument, tutorialKey) {
  const entry = findTutorialEntry(tsUserDocument, tutorialKey);
  if (!entry) return false;
  return entry.completed === true;
}

/**
 * Decides whether to fire the intro modal on (re)login. Returns true
 * only when ALL of these hold:
 *   - the user has access to the feature being tutorial'd
 *     (e.g. `timeTravelAccess === true` for the Time Machine tour)
 *   - the user has not completed THIS tutorial yet
 *
 * Returns false on any partial / loading state — callers should only
 * fire this check after they've confirmed tsUserDocument is populated.
 */
function shouldFireTutorialIntro({
  hasAccess,
  tsUserDocument,
  tutorialKey,
}) {
  if (hasAccess !== true) return false;
  if (!tsUserDocument || typeof tsUserDocument !== "object") return false;
  return !isTutorialCompleted(tsUserDocument, tutorialKey);
}

export { findTutorialEntry, isTutorialCompleted, shouldFireTutorialIntro };
